const DISPOSABLE_DOMAINS = new Set([
  'mailinator.com', 'guerrillamail.com', 'tempmail.com', 'throwaway.email',
  'yopmail.com', 'sharklasers.com', 'guerrillamailblock.com', 'grr.la',
  'dispostable.com', 'trashmail.com', 'fakeinbox.com', 'mailnesia.com',
  'maildrop.cc', 'discard.email', 'temp-mail.org', 'getnada.com',
]);

const VALID_TLDS = new Set([
  'com', 'org', 'net', 'edu', 'gov', 'io', 'co', 'us', 'uk', 'ca',
  'de', 'fr', 'au', 'in', 'jp', 'br', 'it', 'nl', 'se', 'no', 'fi',
  'dk', 'es', 'pt', 'ch', 'at', 'be', 'pl', 'cz', 'ie', 'nz', 'za',
  'mx', 'ar', 'cl', 'il', 'sg', 'hk', 'kr', 'tw', 'info', 'biz', 'me',
  'app', 'dev', 'ai', 'tech', 'xyz', 'online', 'site', 'store', 'pro',
]);

export function validateEmail(email: string): string | null {
  if (!email) {
    return 'Email is required';
  }

  const trimmed = email.trim().toLowerCase();

  // Basic format check
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(trimmed)) {
    return 'Please enter a valid email address';
  }

  const [local, domain] = trimmed.split('@');

  // Local part checks
  if (local.length < 2) {
    return 'Please enter a valid email address';
  }
  if (/^[^a-zA-Z]*$/.test(local)) {
    return 'Please enter a valid email address';
  }

  // Domain checks
  const domainParts = domain.split('.');
  if (domainParts.length < 2) {
    return 'Please enter a valid email address';
  }

  const tld = domainParts[domainParts.length - 1];
  if (!VALID_TLDS.has(tld)) {
    return 'Please enter a valid email address';
  }

  // Domain name (without TLD) must be at least 2 chars
  const domainName = domainParts.slice(0, -1).join('.');
  if (domainName.length < 2) {
    return 'Please enter a valid email address';
  }

  // Block disposable email providers
  if (DISPOSABLE_DOMAINS.has(domain)) {
    return 'Please use a personal or work email address';
  }

  // Catch keyboard-mash patterns: 4+ consecutive consonants with no vowels
  const consonantMash = /[^aeiou\d@.]{5,}/i;
  if (consonantMash.test(local) || consonantMash.test(domainName)) {
    return 'Please enter a real email address';
  }

  return null;
}

export function validatePhone(phone: string): string | null {
  if (!phone) {
    return 'Phone number is required';
  }

  // US phone: 10 digits, optional +1 prefix
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) {
    return null;
  }

  return 'Please enter a valid US phone number';
}

export function validateTextLength(
  text: string,
  minLength = 20,
  maxLength = 1000
): string | null {
  if (!text || text.trim().length === 0) {
    return 'Please describe your financial goals';
  }

  if (text.length < minLength) {
    return `Please write at least ${minLength} characters`;
  }

  if (text.length > maxLength) {
    return `Please keep your description under ${maxLength} characters`;
  }

  return null;
}

export function validateRequired(value: unknown, fieldName = 'This field'): string | null {
  if (value === null || value === undefined || value === '') {
    return `${fieldName} is required`;
  }

  if (Array.isArray(value) && value.length === 0) {
    return `Please select at least one option`;
  }

  return null;
}
